import { formatPhone } from "@/utils/string";
import type { BookingDetailUI } from "../booking-detail.types";

function timeToMinutes(time: string) {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function calcTotalDuration(booking: BookingDetailUI): string {
  const { slots } = booking;
  if (slots.length === 0) return "-";
  const first = timeToMinutes(slots[0].time);
  const last = timeToMinutes(slots[slots.length - 1].endTime);
  const diff = last - first;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  if (m === 0) return `${h}시간`;
  return `${h}시간 ${m}분`;
}

function formatDateKo(dateStr: string) {
  const date = new Date(dateStr);
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 (${dayNames[date.getDay()]})`;
}

function calcDday(dateStr: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "D-Day";
  if (diff > 0) return `D-${diff}`;
  return `D+${Math.abs(diff)}`;
}

function formatDateTime(isoStr: string) {
  const date = new Date(isoStr);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${y}.${m}.${d} ${hh}:${mm}`;
}

interface Props {
  booking: BookingDetailUI;
  openEditCustomerNameModal: () => void;
  onCancel: () => void;
  onShare: () => void;
  onTextShare: () => void;
}

export default function SummaryPanel({ booking, openEditCustomerNameModal, onCancel, onShare, onTextShare }: Props) {
  const totalDuration = calcTotalDuration(booking);
  const weddingDday = booking.weddingDate ? calcDday(booking.weddingDate) : null;
  const shootingDday = booking.shootingDate ? calcDday(booking.shootingDate) : null;

  const statusStyle =
    booking.status === "confirmed"
      ? { bg: "#EFF6FF", text: "#2563EB", border: "#BFDBFE", label: "예약완료" }
      : { bg: "#FEF2F2", text: "#DC2626", border: "#FECACA", label: "취소됨" };
  
  return (
    <div className="space-y-3">
      <div
        className="bg-white rounded-2xl overflow-hidden"
        style={{ border: "1px solid #E5E7EB" }}
      >
        <div
          className="px-5 py-3 flex items-center justify-between"
          style={{ backgroundColor: "#F9FAFB", borderBottom: "1px solid #F3F4F6" }}
        >
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-file-list-3-line text-xs" style={{ color: "#2563EB" }}></i>
            </div>
            <p className="text-sm font-bold" style={{ color: "#111827" }}>예약 정보</p>
          </div>
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ backgroundColor: statusStyle.bg, color: statusStyle.text, border: `1px solid ${statusStyle.border}` }}
          >
            {statusStyle.label}
          </span>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: "#9CA3AF" }}>고객 정보</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: "#6B7280" }}>고객명</span>
                <div className='flex items-center gap-1.5'>
                  <span className="text-sm font-bold" style={{ color: "#111827" }}>{booking.customerName}</span>
                  <button onClick={openEditCustomerNameModal} className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
                    <i className='ri-edit-line text-xl'></i>
                  </button>
                </div>
                
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: "#6B7280" }}>연락처</span>
                <a
                  href={`tel:${booking.customerPhone}`}
                  className="text-sm font-medium cursor-pointer"
                  style={{ color: "#2563EB" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {formatPhone(booking.customerPhone)}
                </a>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: "#6B7280" }}>담당 플래너</span>
                <span className="text-sm font-medium" style={{ color: "#374151" }}>{booking.plannerDisplayName} 플래너</span>
              </div>
            </div>
          </div>

          <div style={{ height: "1px", backgroundColor: "#F3F4F6" }}></div>

          {(booking.weddingDate || booking.shootingDate) && (
            <>
              <div>
                <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: "#9CA3AF" }}>웨딩 일정</p>
                <div className="space-y-2">
                  {booking.shootingDate && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-camera-line text-xs" style={{ color: "#6B7280" }}></i>
                        <span className="text-xs" style={{ color: "#6B7280" }}>촬영 예정일</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {
                          (booking.noShooting == 1) ?  (
                            <p className='text-sm'>미진행</p>
                          ) : 
                          (booking.shootingDateUndecided == 1) ? (
                            <p className='text-sm'>미정</p>
                          ) : (
                            <>
                              <span className="text-sm font-semibold" style={{ color: "#374151" }}>
                                {formatDateKo(booking.shootingDate)}
                              </span>
                              {shootingDday && (
                                <span
                                  className="text-xs px-1.5 py-0.5 rounded-md font-medium"
                                  style={{ backgroundColor: "#F3F4F6", color: "#6B7280" }}
                                >
                                  {shootingDday}
                                </span>
                              )}    
                            </>
                          )
                        }
                        
                      </div>
                    </div>
                  )}
                  {booking.weddingDate && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-heart-line text-xs" style={{ color: "#F59E0B" }}></i>
                        <span className="text-xs" style={{ color: "#6B7280" }}>본식 예정일</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {
                          booking.weddingDateUndecided == 1 ? <p className='text-sm'>미정</p> : (
                            <>
                              <span className="text-sm font-semibold" style={{ color: "#374151" }}>
                                {formatDateKo(booking.weddingDate)}
                              </span>
                              {weddingDday && (
                                <span
                                  className="text-xs px-1.5 py-0.5 rounded-md font-medium"
                                  style={{ backgroundColor: "#FEF3C7", color: "#92400E" }}
                                >
                                  {weddingDday}
                                </span>
                              )}
                            </>
                          )
                        }
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col items-start justify-center">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-sticky-note-line text-xs" style={{ color: "#F59E0B" }}></i>
                      <span className="text-xs" style={{ color: "#6B7280" }}>메모</span>
                    </div>
                    <div className="flex items-center gap-2">

                      {
                        (booking.notice && booking.notice !== '') && <p className='text-sm pt-2' style={{ color: "#374151" }}>{booking.notice}</p>
                      }
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ height: "1px", backgroundColor: "#F3F4F6" }}></div>
            </>
          )}

          <div>
            <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: "#9CA3AF" }}>예약 정보</p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: "#6B7280" }}>예약 코드</span>
                <span className="text-xs font-mono font-semibold" style={{ color: "#374151" }}>{booking.code}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs" style={{ color: "#6B7280" }}>예약 시간</span>
                <span className="text-xs" style={{ color: "#6B7280" }}>{formatDateTime(booking.createdAt)}</span>
              </div>
            </div>
          </div>
          
          {
            booking.status !== 'cancelled' && (
              <button
                onClick={onCancel}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
                style={{ backgroundColor: "#FEF2F2", color: "#DC2626" }}
              >
                <i className="ri-close-circle-line text-sm"></i>
                예약 취소
              </button>
            )
          }
        </div>
      </div>

      <div
        className="bg-white rounded-2xl px-5 py-4"
        style={{ border: "1px solid #E5E7EB", display: 'none'}}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-bar-chart-box-line text-xs" style={{ color: "#2563EB" }}></i>
          </div>
          <p className="text-sm font-bold" style={{ color: "#111827" }}>일정 요약</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-xl px-2 py-3 text-center" style={{ backgroundColor: "#F9FAFB" }}>
            <p className="text-xl font-bold" style={{ color: "#2563EB" }}>{booking.slots.length}</p>
            <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>방문 업체</p>
          </div>
          <div className="rounded-xl px-2 py-3 text-center" style={{ backgroundColor: "#F9FAFB" }}>
            <p className="text-sm font-bold leading-tight" style={{ color: "#111827" }}>{totalDuration}</p>
            <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>총 소요</p>
          </div>
          <div className="rounded-xl px-2 py-3 text-center" style={{ backgroundColor: "#F9FAFB" }}>
            <p className="text-sm font-bold" style={{ color: "#111827" }}>{booking.slots[0]?.time ?? "-"}</p>
            <p className="text-xs mt-0.5" style={{ color: "#9CA3AF" }}>시작 시간</p>
          </div>
        </div>
      </div>

      <div style={{ display: "none" }} />

      <div
        className="bg-white rounded-2xl px-5 py-4"
        style={{ border: "1px solid #E5E7EB" }}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className="ri-share-2-line text-xs" style={{ color: "#2563EB" }}></i>
          </div>
          <p className="text-sm font-bold" style={{ color: "#111827" }}>공유하기</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onShare}
            className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
            style={{ backgroundColor: "#2563EB", color: "white", display: 'none' }}
          >
            <i className="ri-share-line text-sm"></i>
            페이지 공유
          </button>
          <button
            onClick={onTextShare}
            className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
            style={{ backgroundColor: "#F3F4F6", color: "#374151" }}
          >
            <i className="ri-file-copy-line text-sm"></i>
            텍스트 복사
          </button>
        </div>
      </div>

      {booking.status === "confirmed" && (
        <div
          className="bg-white rounded-2xl px-5 py-4"
          style={{ border: "1px solid #E5E7EB", display: 'none' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-settings-3-line text-xs" style={{ color: "#6B7280" }}></i>
            </div>
            <p className="text-sm font-bold" style={{ color: "#111827" }}>예약 관리</p>
          </div>
          <div className="space-y-2">
            <button
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              style={{ backgroundColor: "#F3F4F6", color: "#374151" }}
            >
              <i className="ri-edit-line text-sm"></i>
              일정 수정
            </button>
            <button
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              style={{ backgroundColor: "#F3F4F6", color: "#374151" }}
            >
              <i className="ri-refresh-line text-sm"></i>
              다시 예약
            </button>
            <button
              onClick={onCancel}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              style={{ backgroundColor: "#FEF2F2", color: "#DC2626" }}
            >
              <i className="ri-close-circle-line text-sm"></i>
              예약 취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
}